import { Section } from "anketa-core/group";
import { MessageCollection, Translation } from "anketa-core/i18n";
import { OpenQuestion } from "anketa-core/open_question";
import { OptionsQuestion } from "anketa-core/options_question";
import { MultiSelectOptionsQuestion } from "anketa-core/multiselect_options_question";
import { TextValidator } from "anketa-core/validation";
import {
  AnketaCheckPermissionOptions,
  AnketaResourcesEnum,
} from "../../../../";
import { QuestionsRightsTypes } from "..";

/** Option list key for the "keep forever" compliance frameworks. */
export const COMPLIANCE_GROUP_OPTIONS = "opsanio.retentions.compliance_groups";

/** Framework option key → label. Keys must not contain "." and are written
 * verbatim into `events.keepComplianceGroups`. */
const COMPLIANCE_GROUPS: Array<{ key: string; label: string }> = [
  { key: "gdpr", label: "GDPR" },
  { key: "pci_dss", label: "PCI DSS" },
  { key: "hipaa", label: "HIPAA" },
  { key: "nist_800_53", label: "NIST 800-53" },
  { key: "tsc", label: "TSC (SOC 2)" },
  { key: "gpg13", label: "GPG13" },
];

COMPLIANCE_GROUPS.forEach(({ key, label }) => {
  MessageCollection.Global.setTranslations(
    `${COMPLIANCE_GROUP_OPTIONS}.${key}`,
    [new Translation("en", label), new Translation("de", label)],
  );
});

export const retentionSection = (
  questionsRights: (
    q: QuestionsRightsTypes,
    options?: AnketaCheckPermissionOptions<AnketaResourcesEnum.questionnaires>,
  ) => void,
  setDefaults: boolean,
): Section => {
  const retentions = new Section("retentions", [
    new Translation("en", "Retentions"),
    new Translation("de", "Aufbewahrung"),
  ]);
  retentions.description = [
    new Translation(
      "en",
      `
**What does ‘retention’ mean in the context of logs?**

In log management, the term **retention** refers to the period of time for which log data is stored and retained. The retention policy for logs specifies how long different types of logs - such as system, application, security or audit logs - are kept before they are deleted, archived or otherwise processed.

**Why is the retention of logs important?**

- Legal and regulatory requirements:** Many industries and laws require certain logs to be retained for specific periods of time to ensure traceability and compliance.
- Security and forensics:** Logs are essential for investigating security incidents and tracking changes in the system.
- Storage optimisation:** Setting a retention period prevents logs from growing into infinity and consuming storage resources unnecessarily.

**How does log storage work?**

- Logs are saved for a certain period of time after they are created (e.g. 30, 90 or 365 days).
- At the end of this period, they are either automatically deleted, archived or transferred to another system.
- The retention period can be defined globally for all logs or specifically for certain log types or sources.

Durations use the OpenSearch style: a number plus a unit, e.g. \`365d\` (days), \`90d\`, \`6h\` (hours).`,
    ),
    new Translation(
      "de",
      `
**Was bedeutet "Retention" im Kontext von Logs?**

**Retention** (deutsch: Aufbewahrung) bezeichnet im Log-Management den Zeitraum, über den Logdaten gespeichert und vorgehalten werden. Log-Retention-Policies legen fest, wie lange verschiedene Arten von Logs - etwa System-, Anwendungs-, Sicherheits- oder Audit-Logs - aufbewahrt werden, bevor diese gelöscht, archiviert oder anderweitig verarbeitet werden.

**Warum ist Log-Retention wichtig?**

- **Rechtliche und regulatorische Anforderungen:** Viele Branchen und Gesetze verlangen, dass bestimmte Logs über definierte Zeiträume aufbewahrt werden, um Nachvollziehbarkeit und Compliance sicherzustellen.
- **Sicherheits- und Forensik-Zwecke:** Logs sind essenziell für die Untersuchung von Sicherheitsvorfällen und für die Nachverfolgung von Änderungen im System.
- **Speicheroptimierung:** Durch die Festlegung einer Retention-Periode wird verhindert, dass Logs unbegrenzt wachsen und Speicherressourcen unnötig belegen.

**Wie funktioniert Log-Retention?**

- Logs werden nach ihrer Entstehung für einen festgelegten Zeitraum gespeichert (z. B. 30, 90 oder 365 Tage).
- Nach Ablauf dieser Zeit werden diese entweder automatisch gelöscht, archiviert oder in ein anderes System überführt.
- Die Retention-Periode kann global für alle Logs oder gezielt für bestimmte Log-Typen oder -Quellen festgelegt werden.

Zeitangaben folgen der OpenSearch-Notation: Zahl plus Einheit, z. B. \`365d\` (Tage), \`90d\`, \`6h\` (Stunden).`,
    ),
  ];
  questionsRights(retentions, {
    isInternal: true,
    isExternal: true,
  });

  // ── Events (wazuh-alerts) - opensearch-wazuh-lifecycle ────────────────────
  const events = new Section("events", [
    new Translation("en", "Events (Security Alerts)"),
    new Translation("de", "Events (Sicherheitsereignisse)"),
  ]);
  events.description = [
    new Translation(
      "en",
      "Retention for the Wazuh security-alert stream (`wazuh-alerts-*`), managed by the lifecycle streamer.",
    ),
    new Translation(
      "de",
      "Aufbewahrung des Wazuh-Sicherheitsereignis-Streams (`wazuh-alerts-*`), verwaltet durch den Lifecycle-Streamer.",
    ),
  ];
  questionsRights(events, { isInternal: true, isExternal: true });
  retentions.addChild(events);

  const eventsManage = new OptionsQuestion(
    "manage_lifecycle",
    [
      new Translation("en", "Actively manage retention?"),
      new Translation("de", "Aufbewahrung aktiv verwalten?"),
    ],
    "default.options.yes_no",
  );
  eventsManage.help = [
    new Translation(
      "en",
      "Do you want the lifecycle streamer to actively manage the alert index, or leave it on the legacy shared policy (effectively unmanaged)?",
    ),
    new Translation(
      "de",
      "Soll der Lifecycle-Streamer den Alert-Index aktiv verwalten oder auf der bisherigen gemeinsamen Policy belassen (praktisch unverwaltet)?",
    ),
  ];
  questionsRights(eventsManage, { isInternal: true, isExternal: true });
  events.addChild(eventsManage);

  const eventsRetentionAge = new OpenQuestion("retention_age", [
    new Translation("en", "Final retention (overall)"),
    new Translation("de", "Gesamt-Aufbewahrung"),
  ]);
  eventsRetentionAge.help = [
    new Translation(
      "en",
      "How long must security-alert history be kept overall? Often compliance-driven (ISO 27001 / NIS2 / BSI minimums). e.g. 365d",
    ),
    new Translation(
      "de",
      "Wie lange muss die Historie der Sicherheitsereignisse insgesamt aufbewahrt werden? Häufig compliance-getrieben (ISO 27001 / NIS2 / BSI). z. B. 365d",
    ),
  ];
  eventsRetentionAge.validator = new TextValidator();
  questionsRights(eventsRetentionAge, { isInternal: true, isExternal: true });
  events.addChild(eventsRetentionAge);

  const severityCuts = new OpenQuestion("severity_cuts", [
    new Translation("en", "Severity cut thresholds"),
    new Translation("de", "Schwellenwerte für Schweregrad-Abstufung"),
  ]);
  severityCuts.help = [
    new Translation(
      "en",
      "At what ages should we start dropping lower-severity alerts, and which rule.level survives each cut? Comma-separated AGE:minLevel steps, e.g. `14d:3, 90d:7, 180d:10`. Default: `1d:2`.",
    ),
    new Translation(
      "de",
      "Ab welchem Alter sollen Alerts mit niedrigerem Schweregrad verworfen werden, und welches rule.level überlebt jede Stufe? Kommagetrennte AGE:minLevel-Stufen, z. B. `14d:3, 90d:7, 180d:10`. Standard: `1d:2`.",
    ),
  ];
  severityCuts.validator = new TextValidator();
  questionsRights(severityCuts, { isInternal: true, isExternal: true });
  events.addChild(severityCuts);

  const complianceGroups = new MultiSelectOptionsQuestion(
    "compliance_groups",
    [
      new Translation("en", "Compliance groups to keep forever"),
      new Translation("de", "Compliance-Gruppen dauerhaft aufbewahren"),
    ],
    COMPLIANCE_GROUP_OPTIONS,
  );
  complianceGroups.help = [
    new Translation(
      "en",
      "Which compliance frameworks apply? Alerts tagged with these groups are archived forever - never cut or deleted.",
    ),
    new Translation(
      "de",
      "Welche Compliance-Rahmenwerke gelten? Alerts mit diesen Gruppen werden dauerhaft archiviert - nie abgeschnitten oder gelöscht.",
    ),
  ];
  questionsRights(complianceGroups as QuestionsRightsTypes, {
    isInternal: true,
    isExternal: true,
  });
  events.addChild(complianceGroups);

  // Backstop / deployment knobs - usually not customer-facing.
  const hardCapAge = new OpenQuestion("hard_cap_age", [
    new Translation("en", "Hard cap backstop"),
    new Translation("de", "Absolute Obergrenze"),
  ]);
  hardCapAge.help = [
    new Translation(
      "en",
      "Absolute ceiling age. Leave blank to use 2× the final retention.",
    ),
    new Translation(
      "de",
      "Absolutes Höchstalter. Leer lassen für 2× der Gesamt-Aufbewahrung.",
    ),
  ];
  hardCapAge.validator = new TextValidator();
  questionsRights(hardCapAge, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  events.addChild(hardCapAge);

  const indexPattern = new OpenQuestion("index_pattern", [
    new Translation("en", "Index pattern"),
    new Translation("de", "Index-Muster"),
  ]);
  indexPattern.validator = new TextValidator();
  questionsRights(indexPattern, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  events.addChild(indexPattern);

  const maintenanceInterval = new OpenQuestion("maintenance_interval", [
    new Translation("en", "Maintenance interval"),
    new Translation("de", "Wartungsintervall"),
  ]);
  maintenanceInterval.validator = new TextValidator();
  questionsRights(maintenanceInterval, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  events.addChild(maintenanceInterval);

  // ── Devices (asset inventory) - opensearch-assets ─────────────────────────
  const devices = new Section("devices", [
    new Translation("en", "Devices (Asset Inventory)"),
    new Translation("de", "Geräte (Asset-Inventar)"),
  ]);
  devices.description = [
    new Translation(
      "en",
      "Retention for the device/asset inventory. Managed by default: when disabled the indices stay on the legacy shared policy (unmanaged).",
    ),
    new Translation(
      "de",
      "Aufbewahrung des Geräte-/Asset-Inventars. Standardmäßig verwaltet: wenn deaktiviert, verbleiben die Indizes auf der bisherigen gemeinsamen Policy (unverwaltet).",
    ),
  ];
  questionsRights(devices, { isInternal: true, isExternal: true });
  retentions.addChild(devices);

  const devicesManage = new OptionsQuestion(
    "manage_lifecycle",
    [
      new Translation("en", "Actively manage retention?"),
      new Translation("de", "Aufbewahrung aktiv verwalten?"),
    ],
    "default.options.yes_no",
  );
  devicesManage.help = [
    new Translation(
      "en",
      "Do you want us to manage retention actively, or leave indices on the legacy shared policy (effectively unmanaged)?",
    ),
    new Translation(
      "de",
      "Sollen wir die Aufbewahrung aktiv verwalten oder die Indizes auf der bisherigen gemeinsamen Policy belassen (praktisch unverwaltet)?",
    ),
  ];
  questionsRights(devicesManage, { isInternal: true, isExternal: true });
  devices.addChild(devicesManage);

  const devicesMergedRetention = new OpenQuestion("merged_retention_age", [
    new Translation("en", "Historical inventory retention"),
    new Translation("de", "Aufbewahrung des historischen Inventars"),
  ]);
  devicesMergedRetention.help = [
    new Translation(
      "en",
      "How long should the historical (merged/codex) device inventory be kept? Audit-trail driven - often multi-year. e.g. 365d",
    ),
    new Translation(
      "de",
      "Wie lange soll das historische (zusammengeführte) Geräte-Inventar aufbewahrt werden? Audit-getrieben - oft mehrjährig. z. B. 365d",
    ),
  ];
  devicesMergedRetention.validator = new TextValidator();
  questionsRights(devicesMergedRetention, {
    isInternal: true,
    isExternal: true,
  });
  devices.addChild(devicesMergedRetention);

  const devicesRawRetention = new OpenQuestion("raw_retention_age", [
    new Translation("en", "Raw per-scan retention"),
    new Translation("de", "Aufbewahrung roher Scan-Daten"),
  ]);
  devicesRawRetention.help = [
    new Translation(
      "en",
      "How long do you need raw per-scan documents for troubleshooting before they're dropped? e.g. 4d",
    ),
    new Translation(
      "de",
      "Wie lange werden rohe Scan-Dokumente zur Fehlersuche benötigt, bevor sie verworfen werden? z. B. 4d",
    ),
  ];
  devicesRawRetention.validator = new TextValidator();
  questionsRights(devicesRawRetention, { isInternal: true, isExternal: true });
  devices.addChild(devicesRawRetention);

  const devicesMergedForceMerge = new OpenQuestion("merged_force_merge_age", [
    new Translation("en", "Compaction age (historical)"),
    new Translation("de", "Verdichtungsalter (historisch)"),
  ]);
  devicesMergedForceMerge.validator = new TextValidator();
  questionsRights(devicesMergedForceMerge, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  devices.addChild(devicesMergedForceMerge);

  const devicesRawForceMerge = new OpenQuestion("raw_force_merge_age", [
    new Translation("en", "Compaction age (raw)"),
    new Translation("de", "Verdichtungsalter (roh)"),
  ]);
  devicesRawForceMerge.validator = new TextValidator();
  questionsRights(devicesRawForceMerge, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  devices.addChild(devicesRawForceMerge);

  // ── Flows (network flow records) - opensearch-flows ───────────────────────
  const flows = new Section("flows", [
    new Translation("en", "Flows (Network Flow Records)"),
    new Translation("de", "Flows (Netzwerk-Flow-Daten)"),
  ]);
  flows.description = [
    new Translation(
      "en",
      "Retention for network flow records. Managed by default like devices. NOTE: the flows lifecycle is not honored by the platform yet - captured now so intake is complete once support lands.",
    ),
    new Translation(
      "de",
      "Aufbewahrung von Netzwerk-Flow-Daten. Standardmäßig verwaltet wie bei Geräten. HINWEIS: der Flow-Lifecycle wird von der Plattform noch nicht umgesetzt - bereits erfasst, damit die Aufnahme vollständig ist, sobald die Unterstützung verfügbar ist.",
    ),
  ];
  questionsRights(flows, { isInternal: true, isExternal: true });
  retentions.addChild(flows);

  const flowsManage = new OptionsQuestion(
    "manage_lifecycle",
    [
      new Translation("en", "Actively age out flow data?"),
      new Translation("de", "Flow-Daten aktiv altern lassen?"),
    ],
    "default.options.yes_no",
  );
  flowsManage.help = [
    new Translation(
      "en",
      "Do you want flow data actively aged out, or kept indefinitely on the shared policy?",
    ),
    new Translation(
      "de",
      "Sollen Flow-Daten aktiv gealtert oder unbegrenzt auf der gemeinsamen Policy gehalten werden?",
    ),
  ];
  questionsRights(flowsManage, { isInternal: true, isExternal: true });
  flows.addChild(flowsManage);

  const flowsAggregateRetention = new OpenQuestion("aggregate_retention_age", [
    new Translation("en", "Aggregated-flow retention"),
    new Translation("de", "Aufbewahrung aggregierter Flows"),
  ]);
  flowsAggregateRetention.help = [
    new Translation(
      "en",
      "How long should aggregated/summarized flow trends be kept? (network-forensics / trend use case) e.g. 90d",
    ),
    new Translation(
      "de",
      "Wie lange sollen aggregierte/zusammengefasste Flow-Trends aufbewahrt werden? (Netzwerk-Forensik / Trends) z. B. 90d",
    ),
  ];
  flowsAggregateRetention.validator = new TextValidator();
  questionsRights(flowsAggregateRetention, {
    isInternal: true,
    isExternal: true,
  });
  flows.addChild(flowsAggregateRetention);

  const flowsRawRetention = new OpenQuestion("raw_retention_age", [
    new Translation("en", "Raw-flow retention"),
    new Translation("de", "Aufbewahrung roher Flows"),
  ]);
  flowsRawRetention.help = [
    new Translation(
      "en",
      "How long do you need raw per-flow (5-tuple) records? High volume - usually short. e.g. 7d",
    ),
    new Translation(
      "de",
      "Wie lange werden rohe Flow-Datensätze (5-Tupel) benötigt? Hohes Volumen - üblicherweise kurz. z. B. 7d",
    ),
  ];
  flowsRawRetention.validator = new TextValidator();
  questionsRights(flowsRawRetention, { isInternal: true, isExternal: true });
  flows.addChild(flowsRawRetention);

  const flowsAggregateForceMerge = new OpenQuestion(
    "aggregate_force_merge_age",
    [
      new Translation("en", "Compaction age (aggregated)"),
      new Translation("de", "Verdichtungsalter (aggregiert)"),
    ],
  );
  flowsAggregateForceMerge.validator = new TextValidator();
  questionsRights(flowsAggregateForceMerge, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  flows.addChild(flowsAggregateForceMerge);

  const flowsRawForceMerge = new OpenQuestion("raw_force_merge_age", [
    new Translation("en", "Compaction age (raw)"),
    new Translation("de", "Verdichtungsalter (roh)"),
  ]);
  flowsRawForceMerge.validator = new TextValidator();
  questionsRights(flowsRawForceMerge, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  flows.addChild(flowsRawForceMerge);

  if (setDefaults) {
    // Events: managed by default → "0" = Yes (opsanio/documentation#448).
    eventsManage.setFactValue("0");
    eventsRetentionAge.setFactValue("90d");
    severityCuts.setFactValue("1d:2");
    complianceGroups.setFactValues(
      COMPLIANCE_GROUPS.map(({ key }) => key),
    );
    hardCapAge.setFactValue("180d");
    indexPattern.setFactValue("wazuh-alerts-*");
    maintenanceInterval.setFactValue("6h");

    // Devices: managed by default → "0" = Yes (opsanio/documentation#448).
    devicesManage.setFactValue("0");
    devicesMergedRetention.setFactValue("90d");
    devicesRawRetention.setFactValue("4d");
    devicesMergedForceMerge.setFactValue("1d");
    devicesRawForceMerge.setFactValue("1d");

    // Flows: managed by default → "0" = Yes (opsanio/documentation#448).
    flowsManage.setFactValue("0");
    flowsAggregateRetention.setFactValue("90d");
    flowsRawRetention.setFactValue("7d");
    flowsAggregateForceMerge.setFactValue("1d");
    flowsRawForceMerge.setFactValue("1d");
  }
  return retentions;
};
